import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { useTRPC } from '#src/trpc.js';

export const Route = createFileRoute('/_authorization-gate/tools/admin/customer-bom/')({
	head: () => ({ meta: [{ title: 'Customer BOM • Cadstrom' }] }),
	component: AdminCustomerBomPage,
});

function formatTime(): string {
	return new Date().toLocaleTimeString();
}

function AdminCustomerBomPage() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	const [buildId, setBuildId] = useState<string>('');

	// Section 1
	const [initializeOutput, setInitializeOutput] = useState<unknown>(null);

	// Section 2
	const [filter, setFilter] = useState<string>('');
	const [cursor, setCursor] = useState<string>('');
	const [limit, setLimit] = useState<string>('50');
	const [pageOutput, setPageOutput] = useState<unknown>(null);

	// Section 3
	const [bomItemId, setBomItemId] = useState<string>('');
	const [editor, setEditor] = useState<string>('');
	const [editorStatus, setEditorStatus] = useState<string>('');

	// Section 4
	const [readOnlyFilter, setReadOnlyFilter] = useState<string>('');
	const [readOnlyCursor, setReadOnlyCursor] = useState<string>('');
	const [readOnlyLimit, setReadOnlyLimit] = useState<string>('50');
	const [readOnlyOutput, setReadOnlyOutput] = useState<unknown>(null);

	// Section 5
	const [submitOutput, setSubmitOutput] = useState<unknown>(null);

	const initializeMutation = useMutation(
		trpc.customerEditableBom.initialize.mutationOptions({
			onSuccess: (data) => setInitializeOutput(data),
			onError: (err) => setInitializeOutput({ error: err.message }),
		}),
	);

	const updateMutation = useMutation(
		trpc.customerEditableBom.update.mutationOptions({
			onSuccess: (data) => {
				setEditorStatus(`Saved ${formatTime()}`);
				setEditor(JSON.stringify(stripBomItemId(data.item), null, 2));
			},
			onError: (err) => setEditorStatus(`Save failed: ${err.message}`),
		}),
	);

	const submitMutation = useMutation(
		trpc.customerEditableBom.submit.mutationOptions({
			onSuccess: (data) => setSubmitOutput(data),
			onError: (err) => setSubmitOutput({ error: err.message }),
		}),
	);

	async function handleLoadPage(): Promise<void> {
		setPageOutput(null);
		try {
			const parsedLimit = limit.trim() === '' ? undefined : Number.parseInt(limit, 10);
			const result = await queryClient.fetchQuery(
				trpc.customerEditableBom.getMany.queryOptions({
					buildId,
					referenceDesignatorContains: filter.trim() === '' ? undefined : filter.trim(),
					cursor: cursor.trim() === '' ? undefined : cursor.trim(),
					limit: parsedLimit !== undefined && Number.isFinite(parsedLimit) ? parsedLimit : undefined,
				}),
			);
			setPageOutput(result);
		} catch (err) {
			setPageOutput({ error: err instanceof Error ? err.message : String(err) });
		}
	}

	async function handleLoadRow(): Promise<void> {
		setEditorStatus('');
		try {
			const result = await queryClient.fetchQuery(
				trpc.customerEditableBom.getMany.queryOptions({ buildId, limit: 200 }),
			);
			const match = result.items.find((it) => it.bomItemId === bomItemId);
			if (match === undefined) {
				setEditorStatus('Row not found in first 200; use Section 2 with a cursor to confirm.');
				return;
			}
			setEditor(JSON.stringify(stripStaticMetadataAndId(match), null, 2));
			setEditorStatus(`Loaded ${formatTime()}`);
		} catch (err) {
			setEditorStatus(`Load failed: ${err instanceof Error ? err.message : String(err)}`);
		}
	}

	async function handleLoadReadOnlyPage(): Promise<void> {
		setReadOnlyOutput(null);
		try {
			const parsedLimit = readOnlyLimit.trim() === '' ? undefined : Number.parseInt(readOnlyLimit, 10);
			const filterValue = readOnlyFilter.trim();
			const result = await queryClient.fetchQuery(
				trpc.customerReadOnlyBom.getMany.queryOptions({
					buildId,
					filters:
						filterValue === '' ?
							undefined
						:	[{ field: 'referenceDesignator', operator: 'contains', value: filterValue }],
					cursor: readOnlyCursor.trim() === '' ? undefined : readOnlyCursor.trim(),
					limit: parsedLimit !== undefined && Number.isFinite(parsedLimit) ? parsedLimit : undefined,
				}),
			);
			setReadOnlyOutput(result);
		} catch (err) {
			setReadOnlyOutput({ error: err instanceof Error ? err.message : String(err) });
		}
	}

	function handleSave(): void {
		let parsed: unknown;
		try {
			parsed = JSON.parse(editor);
		} catch (err) {
			setEditorStatus(`Parse error: ${err instanceof Error ? err.message : String(err)}`);
			return;
		}
		updateMutation.mutate({ bomItemId, data: parsed as Parameters<typeof updateMutation.mutate>[0]['data'] });
	}

	return (
		<div className='flex min-h-0 grow flex-col gap-6 overflow-auto p-5'>
			<div className='flex flex-col gap-2'>
				<h2 className='text-lg font-semibold'>Customer BOM debug</h2>
				<p className='text-xs text-gray-a11'>
					Drives the <code>customerEditableBom</code> and <code>customerReadOnlyBom</code> tRPC surfaces end-to-end.
					Output is raw JSON.
				</p>
				<TextField
					size='small'
					label='Build ID'
					value={buildId}
					onChange={(e) => setBuildId(e.target.value)}
					className='max-w-md'
				/>
			</div>

			<section className='flex flex-col gap-2 border-t border-gray-a4 pt-4'>
				<h3 className='text-base font-semibold'>1. Initialize</h3>
				<div>
					<Button
						variant='contained'
						size='small'
						disabled={buildId.trim() === '' || initializeMutation.isPending}
						onClick={() => {
							setInitializeOutput(null);
							initializeMutation.mutate({ buildId });
						}}
					>
						Initialize drafts
					</Button>
				</div>
				{initializeOutput !== null && (
					<pre className='overflow-auto rounded bg-gray-a2 p-2 font-mono text-xs'>
						{JSON.stringify(initializeOutput, null, 2)}
					</pre>
				)}
			</section>

			<section className='flex flex-col gap-2 border-t border-gray-a4 pt-4'>
				<h3 className='text-base font-semibold'>2. Browse page</h3>
				<div className='flex flex-wrap gap-2'>
					<TextField
						size='small'
						label='referenceDesignator filter'
						value={filter}
						onChange={(e) => setFilter(e.target.value)}
					/>
					<TextField
						size='small'
						label='cursor (bomItemId)'
						value={cursor}
						onChange={(e) => setCursor(e.target.value)}
					/>
					<TextField
						size='small'
						label='limit'
						type='number'
						value={limit}
						onChange={(e) => setLimit(e.target.value)}
						className='w-24'
					/>
				</div>
				<div>
					<Button
						variant='contained'
						size='small'
						disabled={buildId.trim() === ''}
						onClick={() => {
							void handleLoadPage();
						}}
					>
						Load page
					</Button>
				</div>
				{pageOutput !== null && (
					<pre className='max-h-96 overflow-auto rounded bg-gray-a2 p-2 font-mono text-xs'>
						{JSON.stringify(pageOutput, null, 2)}
					</pre>
				)}
			</section>

			<section className='flex flex-col gap-2 border-t border-gray-a4 pt-4'>
				<h3 className='text-base font-semibold'>3. Edit a row</h3>
				<TextField
					size='small'
					label='BOM Item ID'
					value={bomItemId}
					onChange={(e) => setBomItemId(e.target.value)}
					className='max-w-md'
				/>
				<div className='flex gap-2'>
					<Button
						variant='outlined'
						size='small'
						disabled={buildId.trim() === '' || bomItemId.trim() === ''}
						onClick={() => {
							void handleLoadRow();
						}}
					>
						Load row
					</Button>
					<Button
						variant='contained'
						size='small'
						disabled={bomItemId.trim() === '' || editor.trim() === '' || updateMutation.isPending}
						onClick={handleSave}
					>
						Save
					</Button>
				</div>
				<textarea
					className='min-h-72 rounded border border-gray-a6 bg-gray-a1 p-2 font-mono text-xs'
					value={editor}
					onChange={(e) => setEditor(e.target.value)}
					placeholder='Click "Load row" to populate from the server; then edit the JSON and click "Save". Click "Load row" again to verify persistence.'
					spellCheck={false}
				/>
				{editorStatus !== '' && <p className='text-xs text-gray-a11'>{editorStatus}</p>}
			</section>

			<section className='flex flex-col gap-2 border-t border-gray-a4 pt-4'>
				<h3 className='text-base font-semibold'>4. Browse read-only BOM</h3>
				<p className='text-xs text-gray-a11'>
					Hits the <code>customerReadOnlyBom.getMany</code> tRPC endpoint — constructed dynamically from the live
					BomItem store for the build (never sourced from the draft).
				</p>
				<div className='flex flex-wrap gap-2'>
					<TextField
						size='small'
						label='referenceDesignator filter'
						value={readOnlyFilter}
						onChange={(e) => setReadOnlyFilter(e.target.value)}
					/>
					<TextField
						size='small'
						label='cursor (bomItemId)'
						value={readOnlyCursor}
						onChange={(e) => setReadOnlyCursor(e.target.value)}
					/>
					<TextField
						size='small'
						label='limit'
						type='number'
						value={readOnlyLimit}
						onChange={(e) => setReadOnlyLimit(e.target.value)}
						className='w-24'
					/>
				</div>
				<div>
					<Button
						variant='contained'
						size='small'
						disabled={buildId.trim() === ''}
						onClick={() => {
							void handleLoadReadOnlyPage();
						}}
					>
						Load page
					</Button>
				</div>
				{readOnlyOutput !== null && (
					<pre className='max-h-96 overflow-auto rounded bg-gray-a2 p-2 font-mono text-xs'>
						{JSON.stringify(readOnlyOutput, null, 2)}
					</pre>
				)}
			</section>

			<section className='flex flex-col gap-2 border-t border-gray-a4 pt-4'>
				<h3 className='text-base font-semibold'>5. Submit</h3>
				<p className='text-xs text-gray-a11'>
					Kicks off the <code>customerEditableBom.submit</code> tRPC mutation, which starts the customer-BOM merge
					workflow on Temporal.
				</p>
				<div>
					<Button
						variant='contained'
						size='small'
						disabled={buildId.trim() === '' || submitMutation.isPending}
						onClick={() => {
							setSubmitOutput(null);
							submitMutation.mutate({ buildId });
						}}
					>
						Submit BOM
					</Button>
				</div>
				{submitOutput !== null && (
					<pre className='overflow-auto rounded bg-gray-a2 p-2 font-mono text-xs'>
						{JSON.stringify(submitOutput, null, 2)}
					</pre>
				)}
			</section>
		</div>
	);
}

function stripStaticMetadataAndId<T extends { staticMetadata: unknown; bomItemId: string }>(
	item: T,
): Omit<T, 'staticMetadata' | 'bomItemId'> {
	const { staticMetadata, bomItemId, ...rest } = item;
	return rest;
}

function stripBomItemId<T extends { bomItemId: string }>(item: T): Omit<T, 'bomItemId'> {
	const { bomItemId, ...rest } = item;
	return rest;
}
